import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useParams, useLocation } from 'react-router-dom'
import { format } from 'date-fns'
import PropTypes from 'prop-types'
import {
  makeStyles,
  Typography,
  Button,
  Grid,
  Tooltip,
  Fab,
  IconButton,
  CircularProgress,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@material-ui/core'
import MicIcon from '@material-ui/icons/Mic'
import MicOffIcon from '@material-ui/icons/MicOff'
import VolumeUpIcon from '@material-ui/icons/VolumeUp'
import VolumeOffIcon from '@material-ui/icons/VolumeOff'
import useGetLocalStorage from '../../../hooks/useGetLocalStorage'
import useHistoryPush from '../../../hooks/useHistoryPush'
import localStorageIds from '../../../utils/constants/localStorageIds'
import getStudioJoinInfo from '../../../Apis/studio/getStudioJoinInfo'
import getStudioGames from '../../../Apis/studio/getStudioGames'
import useStudioRoom, {
  STUDIO_ROOM_STATE,
  STUDIO_PREVIEW_STATE,
  STUDIO_MIC_DELIVERY,
  MIC_GAIN_MAX,
} from '../../../hooks/useStudioRoom'
import UnauthorizedPage from '../../Unauthorized/UnauthorizedPage'
import ContainerPadding from '../../../components/containerPadding/containerPadding'
import LoaderDots from '../../../components/loaders/LoaderDots/LoaderDots'
import COLORS from '../../../utils/constants/colors'
import AudioLevelMeter from './AudioLevelMeter'
import NetworkBars from './NetworkBars'
import ScoreboardControls from './ScoreboardControls'
import ScoreboardSourcePicker from './ScoreboardSourcePicker'
import AdVideoPanel from './AdVideoPanel'
import AutoBreakBanner from './AutoBreakBanner'
import StudioRail from './StudioRail'
import { Crosshair, Film, Layers, Pause, Play } from './railIcons'
import OverlayPanel from './OverlayPanel'
import { getOverlayCards } from '../../../Apis/overlays/streamCards'
import useOverlayCard from './useOverlayCard'
import QueryKeys from '../../../utils/constants/queryKeys'
import setScoreboardSource from '../../../Apis/scoreboard/setScoreboardSource'
import getLivestream from '../../../Apis/livestream/getLivestream'
import pauseLivestream from '../../../Apis/livestream/pauseLivestream'
import updateLivestreamFeatures from '../../../Apis/livestream/updateLivestreamFeatures'
import resumeLivestream from '../../../Apis/livestream/resumeLivestream'
import BROADCAST_STATES from '../../../utils/constants/broadcastStates'
import scoreboardSourceOptions, {
  SCOREBOARD_SOURCE_OFF,
  SCOREBOARD_SOURCE_STUDIO,
} from '../../../utils/helpers/eventHelpers/scoreboardSourceOptions'
import useStudioScoreboard from '../../../hooks/useStudioScoreboard'
import useStreamState from '../../../hooks/useStreamState'
import useStreamAdVideo from '../../../hooks/useStreamAdVideo'
import useStreamAdQueue from '../../../hooks/useStreamAdQueue'

const useStyles = makeStyles({
  topBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: '0 0 auto',
  },
  backBtn: {
    flexShrink: 0,
    color: COLORS.grey,
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
    textAlign: 'center',
  },
  titleText: {
    fontWeight: 700,
  },
  subtitleText: {
    color: COLORS.grey,
  },
  topBarRight: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minWidth: '120px',
  },
  // THEATER: the whole view is one black stage that fills the height available below the
  // app chrome (measured at runtime — see colHeight). The 16:9 video is letterboxed inside
  // it; the title and a compact floating control pill sit on top — so the commentator sees
  // a maximised video AND the controls + their state at all times, without scrolling.
  stage: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: COLORS.black,
    borderRadius: '10px',
    overflow: 'hidden',
    boxShadow: '0 8px 28px rgba(0, 0, 0, 0.25)',
    // height set inline (measured colHeight); the video area flexes and the control bar
    // is fixed beneath it, so controls never overlap the video or its rendered scoreboard.
  },
  videoArea: {
    position: 'relative',
    flex: '1 1 auto',
    minHeight: 0,
  },
  topScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '96px',
    zIndex: 1,
    background: 'linear-gradient(rgba(0, 0, 0, 0.55), transparent)',
    pointerEvents: 'none',
  },
  topline: {
    position: 'absolute',
    top: '12px',
    left: '16px',
    right: '16px',
    zIndex: 3,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  backBtnTheater: {
    flexShrink: 0,
    minWidth: 0,
    padding: '4px 8px',
    color: 'rgba(255, 255, 255, 0.85)',
    '&:hover': { background: 'rgba(255, 255, 255, 0.14)' },
  },
  titleOverlay: {
    flex: 1,
    minWidth: 0,
    color: COLORS.white,
    textShadow: '0 1px 4px rgba(0, 0, 0, 0.6)',
  },
  titleOverlayText: { fontWeight: 700, lineHeight: 1.2 },
  titleOverlaySub: { color: 'rgba(255, 255, 255, 0.72)' },
  liveBadge2: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.06em',
    color: COLORS.white,
    background: 'rgba(0, 0, 0, 0.6)',
  },
  dot: {
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    display: 'inline-block',
    flexShrink: 0,
  },
  liveDot: { background: COLORS.red },
  pausedDot: { background: COLORS.darkYellow },
  // Control bar BELOW the video (never over it — keeps the rendered scoreboard visible).
  // A row of controls separated by space alone (no dividers); the Network and Level
  // indicators carry their label above them. Controls share a bottom baseline.
  controlBar: {
    flex: '0 0 auto',
    display: 'flex',
    flexWrap: 'wrap',
    // Bottoms aligned rather than centres: each section is a column headed by its own label, and
    // the scoreboard's header carries the source picker, so the columns are different heights.
    alignItems: 'flex-end',
    justifyContent: 'center',
    // Tight enough that the scoreboard and the media controls share one line on a laptop; the
    // wrap is still there, but as a last resort for narrow windows rather than the normal case.
    gap: '20px',
    // The bottom leaves room for the scoreboard's status line, which floats under the cards
    // rather than sitting in flow — in flow it widened the row enough to wrap the bar.
    padding: '14px 22px 28px',
    borderTop: '1px solid rgba(255, 255, 255, 0.12)',
    background: '#121419',
  },
  // The bar holds two jobs — tracking the game, and speaking over it. They are labelled and
  // separated so neither reads as part of the other.
  barSection: { display: 'flex', flexDirection: 'column', gap: '8px' },
  // The label row of a section. The scoreboard's also holds the source picker, so it is a real
  // row rather than a floating caption.
  sectionHeader: { display: 'flex', alignItems: 'center', gap: '10px', height: '22px' },
  sectionBody: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '18px',
    minHeight: '80px',
  },
  barDivider: {
    alignSelf: 'stretch',
    width: '1px',
    margin: '4px 0',
    background: 'rgba(255, 255, 255, 0.12)',
  },
  // A control with its label floating directly above it, so all controls stay centred on
  // one line — the label never shifts their vertical alignment.
  labeledCtrl: {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
  },
  // Caption floating above a single control (Network, Level), so the controls either side of it
  // stay centred on one line.
  ctrlLabel: {
    position: 'absolute',
    top: '-15px',
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, 0.5)',
    whiteSpace: 'nowrap',
  },
  // Heading of a whole section, in flow: the scoreboard's shares its row with the source picker.
  sectionLabel: {
    fontFamily: "'Fira Sans Condensed', 'Roboto Condensed', Roboto, sans-serif",
    fontSize: '12px',
    fontWeight: 500,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#9aa1aa',
    whiteSpace: 'nowrap',
  },
  ctrlRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  ctrlStatus: {
    fontSize: '12.5px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  muteBtnBar: {
    color: COLORS.white,
    background: 'rgba(255, 255, 255, 0.1)',
    '&:hover': { background: 'rgba(255, 255, 255, 0.2)' },
  },
  // Involuntary state messages, as a centred toast over the video.
  toast: {
    position: 'absolute',
    top: '58px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 3,
    maxWidth: 'calc(100% - 32px)',
    padding: '8px 16px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    textAlign: 'center',
    background: COLORS.darkYellow,
    color: COLORS.black,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
  },
  toastDanger: { background: COLORS.red, color: COLORS.white },
  gainSliderDark: {
    width: '92px',
    color: COLORS.orange,
    padding: '12px 0',
    '& .MuiSlider-rail': {
      backgroundColor: 'rgba(255, 255, 255, 0.25)',
      opacity: 1,
      height: '4px',
      borderRadius: '2px',
    },
    '& .MuiSlider-track': { backgroundColor: COLORS.orange, height: '4px', borderRadius: '2px' },
    '& .MuiSlider-mark': {
      backgroundColor: 'rgba(255, 255, 255, 0.45)',
      height: '8px',
      width: '2px',
      borderRadius: '1px',
      marginTop: '-2px',
    },
    '& .MuiSlider-thumb': {
      width: '13px',
      height: '13px',
      marginTop: '-5px',
      backgroundColor: COLORS.white,
      border: `2px solid ${COLORS.orange}`,
    },
  },
  // Live volume number while dragging; turns into an amber "Boost" note when over 100.
  // Fixed width so the slider doesn't shift when it appears/disappears.
  gainReadout: {
    minWidth: '38px',
    fontSize: '11px',
    fontWeight: 700,
    color: 'rgba(255, 255, 255, 0.85)',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
  },
  gainReadoutBoost: {
    color: COLORS.darkOrange,
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    background: COLORS.black,
    display: 'block',
  },
  overlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    textAlign: 'center',
    padding: '24px',
    background: 'rgba(0, 0, 0, 0.62)',
    color: COLORS.white,
  },
  overlayText: {
    color: COLORS.white,
  },
  // Light grey over the preview while an ad video stands in for the stream.
  adScrim: {
    position: 'absolute',
    inset: 0,
    zIndex: 2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    textAlign: 'center',
    padding: '24px',
    background: 'rgba(226, 228, 233, 0.8)',
    color: '#1b1d22',
    pointerEvents: 'none',
  },
  adScrimFile: { fontWeight: 700 },
  // Mic-delivery status ring (Option B): a coloured halo around the orange mic button —
  // amber connecting, green on air, red not reaching. The button keeps its solid orange +
  // heartbeat; only this ring (and the one-word label below) carries the delivery state.
  micRing: {
    position: 'absolute',
    top: '-6px',
    left: '-6px',
    right: '-6px',
    bottom: '-6px',
    borderRadius: '50%',
    border: '3px solid transparent',
    pointerEvents: 'none',
  },
  ringConnecting: { borderColor: COLORS.darkOrange },
  ringLive: { borderColor: COLORS.green },
  ringNotReaching: { borderColor: COLORS.errorRed },
  micButtonWrap: {
    position: 'relative',
    display: 'inline-flex',
  },
  // The commentary mode (On-site / Off-site), floating under the mic like the captions above
  // Network and Level, so the bar keeps its height.
  micModeLabel: {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    marginTop: '8px',
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, 0.5)',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
  },
  micFab: {
    width: '52px',
    height: '52px',
    // MUI's disabled Fab is dark-on-dark on this bar; keep it readable but clearly inactive.
    '&&.Mui-disabled': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      color: 'rgba(255, 255, 255, 0.45)',
    },
  },
  // While on air the mic button is a solid colour with a soft heartbeat ring — a calm,
  // constant "you are live" cue. Delivery state is carried by the coloured ring around
  // it (ringConnecting / ringLive / ringNotReaching) + the word beside it, not by
  // recolouring the button.
  micFabOn: {
    backgroundColor: COLORS.orange,
    color: COLORS.white,
    animation: '$pulseRing 1.8s infinite',
    '&:hover': { backgroundColor: COLORS.orange },
  },
  errorText: {
    color: COLORS.red,
    textAlign: 'center',
    marginTop: '12px',
  },
  '@keyframes pulseRing': {
    '0%': { boxShadow: '0 0 0 0 rgba(224, 114, 110, 0.5)' },
    '70%': { boxShadow: '0 0 0 14px rgba(224, 114, 110, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(224, 114, 110, 0)' },
  },
})

const gameTitle = (game) => {
  if (!game) return 'Commentary'
  // `eventTeams` always carries the names — `teams` drops ad-hoc teams that aren't
  // linked to a Team entity (e.g. "TEST"), so prefer eventTeams; home team first.
  const teams = game.eventTeams?.length ? game.eventTeams : game.teams || []
  const names = [...teams]
    .sort((a, b) => (b?.isHomeTeam ? 1 : 0) - (a?.isHomeTeam ? 1 : 0))
    .map((t) => t?.name)
    .filter(Boolean)
  if (names.length >= 2) return `${names[0]} vs ${names[1]}`
  if (names.length === 1) return names[0]
  return game.title || 'Commentary'
}

const gameSubtitle = (game) => {
  if (!game) return ''
  const date = game.startDateTime ? format(new Date(game.startDateTime), 'dd MMM yyyy, HH:mm') : ''
  return [date, game.place].filter(Boolean).join('  ·  ')
}

// Health copy shown beside the mic button while on air. "Disconnected" is the case
// where the mic is enabled locally but its audio is not reaching the broadcast.
const DELIVERY_LABEL = {
  [STUDIO_MIC_DELIVERY.CONNECTING]: 'Connecting…',
  [STUDIO_MIC_DELIVERY.LIVE]: 'On Air',
  [STUDIO_MIC_DELIVERY.NOT_REACHING]: 'Disconnected',
}

const Loader = () => (
  <Grid container justifyContent='center' style={{ marginTop: '100px' }}>
    <LoaderDots style={{ fill: COLORS.orange, width: 70, height: 70 }} />
  </Grid>
)

const StudioTopBar = ({ title, subtitle, onBack, right }) => {
  const classes = useStyles()
  return (
    <div className={classes.topBar}>
      <Button variant='text' onClick={onBack} className={classes.backBtn}>
        ← Back to games
      </Button>
      <div className={classes.titleBlock}>
        <Typography variant='h6' noWrap className={classes.titleText}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant='body2' noWrap className={classes.subtitleText}>
            {subtitle}
          </Typography>
        ) : null}
      </div>
      <div className={classes.topBarRight}>{right}</div>
    </div>
  )
}

StudioTopBar.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  right: PropTypes.node,
}

StudioTopBar.defaultProps = { title: '', subtitle: '', right: null }

export const StudioRoomView = ({
  joinInfo,
  game,
  orgId,
  gameId,
  title,
  subtitle,
  onBack,
  onTokenExpiring,
}) => {
  const classes = useStyles()
  const {
    videoRef,
    roomState,
    previewState,
    isOnAir,
    micDelivery,
    droppedOffAir,
    takenOffAir,
    modeChanged,
    commentaryMode,
    followCommentaryMode,
    micTrack,
    networkLevel,
    previewMuted,
    micBusy,
    micGain,
    setMicGain,
    gainSupported,
    error,
    toggleCommentary,
    togglePreviewMute,
  } = useStudioRoom(joinInfo, onTokenExpiring)

  // The manual scoreboard is one of several sources a game's overlay can take, and the operator
  // chooses between them from here as well as from the event drawer — the same endpoint, and the
  // same permission, since whoever is on air is the person who knows when to switch.
  //
  // The event's own choice is the starting value. A switch made here is answered by the server
  // before it is believed, and held locally afterwards: the games list this page reads is cached
  // and may even have arrived through navigation state, so it will not reflect the change.
  const broadcast = game?.broadcast
  const [chosenSource, setChosenSource] = useState(null)
  const source = chosenSource ?? broadcast?.scoreboardSource ?? SCOREBOARD_SOURCE_OFF
  const sourceOptions = useMemo(
    () => scoreboardSourceOptions(broadcast, null, broadcast?.type),
    [broadcast],
  )
  // Nothing to show when the board can never be tracked from here — a court-fed game keeps its
  // sources in the drawer, and the Studio panel would be permanently locked.
  const canTrackFromStudio = sourceOptions.some(
    (option) => option.value === SCOREBOARD_SOURCE_STUDIO,
  )
  const manualScoreboard = source === SCOREBOARD_SOURCE_STUDIO
  const sourceMutation = useMutation((next) => setScoreboardSource(orgId, gameId, next))
  const chooseSource = (next) => {
    const previous = source
    setChosenSource(next)
    sourceMutation.mutate(next, { onError: () => setChosenSource(previous) })
  }
  // Which cards this stream may put on air is the backend's call — it knows whether stats back
  // the game at all, and how each board is branded. An empty answer means no rack is drawn.
  const streamId = broadcast?.id ?? null
  const { data: overlayCards = [] } = useQuery(
    [QueryKeys.GET_OVERLAY_CARDS, orgId, streamId],
    () => getOverlayCards(orgId, streamId),
    { enabled: Boolean(orgId && streamId), staleTime: Infinity },
  )
  // The card on air, and the one the streamer is still rendering. A card it never draws is
  // hidden again and named in the rack.
  const [failedCard, setFailedCard] = useState(null)
  const overlayCard = useOverlayCard(orgId, streamId, { onFailed: setFailedCard })
  const { activeCard, pending: pendingCard } = overlayCard
  const changeCard = (next) => {
    setFailedCard(null)
    overlayCard.changeCard(next)
  }

  // Whether the stream is running or paused is read from the stream itself — the games list this
  // page has is cached — then kept current by the stream's live hub.
  const queryClient = useQueryClient()
  const streamKey = [QueryKeys.GET_LIVESTREAM, streamId]
  const { data: liveStream } = useQuery(streamKey, () => getLivestream({ broadcastId: streamId, orgId }), {
    enabled: Boolean(streamId && orgId),
    refetchOnWindowFocus: true,
    retry: false,
  })
  useStreamState(orgId, streamId, Boolean(streamId))
  const savedCommentaryMode = liveStream?.commentaryMode
  useEffect(() => {
    if (savedCommentaryMode) followCommentaryMode(savedCommentaryMode)
  }, [savedCommentaryMode, followCommentaryMode])
  const streamState = liveStream?.state ?? broadcast?.state ?? null
  const streamPaused = streamState === BROADCAST_STATES.paused
  const streamToggle = useMutation(
    (paused) => (paused ? resumeLivestream(streamId, orgId) : pauseLivestream(streamId, orgId)),
    {
      // Believed at once; the push or the next read settles it.
      onMutate: (paused) =>
        queryClient.setQueryData(streamKey, (current) => ({
          ...current,
          state: paused ? BROADCAST_STATES.running : BROADCAST_STATES.paused,
        })),
      onSettled: () => queryClient.invalidateQueries(streamKey),
    },
  )
  const autofollowOn = liveStream?.autofollowEnabled ?? broadcast?.autofollowEnabled ?? true
  const autofollowToggle = useMutation(
    (on) => updateLivestreamFeatures(streamId, { autofollow: on }, orgId),
    {
      onMutate: (on) =>
        queryClient.setQueryData(streamKey, (current) => ({ ...current, autofollowEnabled: on })),
      onSettled: () => queryClient.invalidateQueries(streamKey),
    },
  )

  // The videos uploaded to this game; the channel plays them from the same list.
  const adVideos = useMemo(
    () => (game?.media ?? []).filter((media) => media.mediaType === 'Video'),
    [game],
  )
  const { adVideo } = useStreamAdVideo(orgId, streamId, Boolean(streamId))
  const adQueue = useStreamAdQueue(orgId, streamId, Boolean(streamId), Boolean(adVideo))
  const adVideosQueued = adQueue.queue.upNext.filter(
    (item) => item.source === 'ClientAd' && item.state !== 'Playing',
  ).length

  const scoreboard = useStudioScoreboard(orgId, gameId, manualScoreboard)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [micAcknowledged, setMicAcknowledged] = useState(false)
  // Show the live volume number only while the fader is being dragged.
  const [adjusting, setAdjusting] = useState(false)

  // Size the view to the height actually available below the app chrome, so the video +
  // console always fit without scrolling. We measure our own top offset rather than
  // hardcode the app bar / padding height (which differs by build and screen).
  const layoutRef = useRef(null)
  const [colHeight, setColHeight] = useState('auto')
  useLayoutEffect(() => {
    const recompute = () => {
      const el = layoutRef.current
      if (!el) return
      const top = el.getBoundingClientRect().top
      // Subtract the wrapper's own bottom padding precisely (it's a % of width, so it
      // grows on wide screens — guessing it would re-introduce a scrollbar), plus a few px.
      const parent = el.parentElement
      const padBottom = parent ? parseFloat(window.getComputedStyle(parent).paddingBottom) || 0 : 0
      const avail = window.innerHeight - top - padBottom - 8
      setColHeight(`${Math.max(420, Math.round(avail))}px`)
    }
    recompute()
    window.addEventListener('resize', recompute)
    return () => window.removeEventListener('resize', recompute)
  }, [])

  const isConnected = roomState === STUDIO_ROOM_STATE.CONNECTED
  const showConnectingOverlay =
    roomState === STUDIO_ROOM_STATE.CONNECTING || roomState === STUDIO_ROOM_STATE.IDLE
  const showDisconnectedOverlay =
    roomState === STUDIO_ROOM_STATE.DISCONNECTED || roomState === STUDIO_ROOM_STATE.RECONNECTING
  const showWaitingOverlay = isConnected && previewState === STUDIO_PREVIEW_STATE.NONE
  const showNoVideoOverlay = isConnected && previewState === STUDIO_PREVIEW_STATE.NO_VIDEO
  const showLiveBadge = isConnected && (previewState === STUDIO_PREVIEW_STATE.LIVE || streamPaused)
  const showInterruptedBanner = isConnected && previewState === STUDIO_PREVIEW_STATE.INTERRUPTED

  // An on-site game is commentated at the court, from the stream app or from here: the voice is
  // placed by when it was spoken, so the commentator follows the court, not the preview. The mode
  // is named under the mic, as in the game form.
  const commentaryModeLabel = commentaryMode === 'OnSite' ? 'On-site' : 'Off-site'
  const micDisabled = !isConnected
  const micTooltip = !isConnected
    ? 'Connect to the studio to go on air'
    : isOnAir
      ? 'Mute your microphone — stops your commentary'
      : 'Go on air — your microphone will be mixed into the stream'
  const micIcon = isOnAir ? <MicIcon /> : <MicOffIcon />

  // Mic-delivery state. The button itself stays solid orange + heartbeat while on air
  // (see micFabOn); the state is carried by a coloured RING around it (ringStateClass)
  // plus the one-word label below. "Disconnected" = mic on locally but not reaching the
  // broadcast.
  const deliveryLabel = DELIVERY_LABEL[micDelivery] || ''
  const ringStateClass =
    {
      [STUDIO_MIC_DELIVERY.LIVE]: classes.ringLive,
      [STUDIO_MIC_DELIVERY.CONNECTING]: classes.ringConnecting,
      [STUDIO_MIC_DELIVERY.NOT_REACHING]: classes.ringNotReaching,
    }[micDelivery] || ''
  // Status word colour mirrors the ring (the ring is the primary cue; this backs it up).
  const deliveryColor =
    {
      [STUDIO_MIC_DELIVERY.LIVE]: COLORS.green,
      [STUDIO_MIC_DELIVERY.CONNECTING]: COLORS.darkOrange,
      [STUDIO_MIC_DELIVERY.NOT_REACHING]: COLORS.errorRed,
    }[micDelivery] || COLORS.white

  const onMicClick = () => {
    if (micBusy) return // a publish / mute / unmute is in flight
    // Going on air is deliberate: confirm once per session, then toggle freely.
    if (isOnAir || micAcknowledged) {
      toggleCommentary()
      return
    }
    setConfirmOpen(true)
  }
  const confirmGoOnAir = () => {
    setConfirmOpen(false)
    setMicAcknowledged(true)
    toggleCommentary()
  }

  const streamRunning = streamState === BROADCAST_STATES.running
  const railItems = [
    {
      key: 'stream',
      label: streamPaused ? 'Resume the stream' : 'Pause the stream',
      icon: streamPaused ? <Play /> : <Pause />,
      tone: streamPaused ? 'amber' : undefined,
      busy: streamToggle.isLoading,
      disabled: !streamRunning && !streamPaused,
      disabledReason: 'The stream is not running',
      onPress: () => streamToggle.mutate(streamPaused),
    },
    {
      key: 'autofollow',
      label: autofollowOn ? 'Turn autofollow off' : 'Turn autofollow on',
      icon: <Crosshair />,
      on: autofollowOn,
      busy: autofollowToggle.isLoading,
      onPress: () => autofollowToggle.mutate(!autofollowOn),
    },
    {
      key: 'ad-videos',
      label: 'Ad videos',
      icon: <Film />,
      live: adVideo?.source === 'ClientAd',
      tone: adVideo?.source === 'MismatchAd' ? 'neutral' : undefined,
      badge: adVideosQueued,
      disabled: adVideos.length === 0,
      disabledReason: 'No ad videos uploaded for this game',
      content: (
        <AdVideoPanel
          videos={adVideos}
          upNext={adQueue.queue.upNext}
          live={streamRunning || streamPaused}
          adding={adQueue.adding}
          removing={adQueue.removing}
          starting={adQueue.starting}
          stopped={adQueue.stopped}
          onAdd={adQueue.add}
          onRemove={adQueue.remove}
          onStart={adQueue.start}
        />
      ),
    },
    {
      key: 'overlays',
      label: 'Stats overlays',
      icon: <Layers />,
      live: Boolean(activeCard),
      disabled: overlayCards.length === 0,
      disabledReason: 'Stats overlays are not available for this game',
      content: (
        <OverlayPanel
          cards={overlayCards}
          active={activeCard}
          pending={pendingCard}
          failed={failedCard}
          onShow={changeCard}
          onHide={() => changeCard(null)}
          busy={overlayCard.busy}
        />
      ),
    },
  ]

  return (
    <ContainerPadding paddingValue='1.5%' vertical>
      <div ref={layoutRef} className={classes.stage} style={{ height: colHeight }}>
        <div className={classes.videoArea}>
          {/* Always mounted so the preview track attaches reliably; overlays sit on top. */}
          <video ref={videoRef} className={classes.video} autoPlay playsInline muted />

          <div className={classes.topScrim} />
          <div className={classes.topline}>
            <Button variant='text' onClick={onBack} className={classes.backBtnTheater}>
              ← Back
            </Button>
            <div className={classes.titleOverlay}>
              <Typography variant='subtitle1' noWrap className={classes.titleOverlayText}>
                {title}
              </Typography>
              {subtitle ? (
                <Typography
                  variant='caption'
                  component='div'
                  noWrap
                  className={classes.titleOverlaySub}
                >
                  {subtitle}
                </Typography>
              ) : null}
            </div>
            {showLiveBadge && (
              <span className={classes.liveBadge2}>
                <span
                  className={`${classes.dot} ${streamPaused ? classes.pausedDot : classes.liveDot}`}
                />
                {streamPaused ? 'PAUSED' : 'LIVE'}
              </span>
            )}
          </div>

          {showConnectingOverlay && (
            <div className={classes.overlay}>
              <LoaderDots style={{ fill: COLORS.white, width: 50, height: 50 }} />
              <Typography className={classes.overlayText}>Connecting to the studio…</Typography>
            </div>
          )}
          {showWaitingOverlay && (
            <div className={classes.overlay}>
              <Typography variant='h6' className={classes.overlayText}>
                Waiting for the stream to start…
              </Typography>
              <Typography variant='body2' className={classes.overlayText}>
                The preview appears here as soon as the stream is live.
              </Typography>
            </div>
          )}
          {showNoVideoOverlay && (
            <div className={classes.overlay}>
              <LoaderDots style={{ fill: COLORS.white, width: 50, height: 50 }} />
              <Typography variant='h6' className={classes.overlayText}>
                Preview stalled, recovering…
              </Typography>
              <Typography variant='body2' className={classes.overlayText}>
                {isOnAir ? 'You’re still on air.' : 'Your mic is off.'}
              </Typography>
            </div>
          )}
          {showDisconnectedOverlay && (
            <div className={classes.overlay}>
              <Typography variant='h6' className={classes.overlayText}>
                {roomState === STUDIO_ROOM_STATE.RECONNECTING ? 'Reconnecting…' : 'Disconnected'}
              </Typography>
              <Typography variant='body2' className={classes.overlayText}>
                {roomState === STUDIO_ROOM_STATE.RECONNECTING
                  ? 'Your microphone is paused — you are not on air.'
                  : 'Your microphone is paused. Please refresh the page to reconnect.'}
              </Typography>
            </div>
          )}

          {adVideo && (
            <div className={classes.adScrim} role='status'>
              <Typography variant='h6'>Your viewers are watching an ad video</Typography>
              {adVideo.filename && (
                <Typography variant='subtitle1' className={classes.adScrimFile}>
                  {adVideo.filename}
                </Typography>
              )}
            </div>
          )}

          {/* transient messages as toasts over the video TOP — never the bottom (scoreboard) */}
          <AutoBreakBanner at={adQueue.autoBreakAt} className={classes.toast} />
          {droppedOffAir && (
            <div className={`${classes.toast} ${classes.toastDanger}`}>
              You’ve gone off air — the stream restarted. Tap the mic to go back on air.
            </div>
          )}
          {takenOffAir && !isOnAir && (
            <div className={`${classes.toast} ${classes.toastDanger}`}>
              You were taken off air. The game is commentated from the mobile app.
            </div>
          )}
          {modeChanged && !isOnAir && (
            <div className={`${classes.toast} ${classes.toastDanger}`}>
              You were taken off air: the game is now commentated{' '}
              {commentaryMode === 'OnSite' ? 'on-site' : 'off-site'}. Tap the mic to go back on air.
            </div>
          )}
          {showInterruptedBanner && !droppedOffAir && (
            <div className={classes.toast}>
              Preview interrupted —{' '}
              {isOnAir ? 'your commentary is still on air.' : 'commentary is off.'}
            </div>
          )}
          {micDelivery === STUDIO_MIC_DELIVERY.NOT_REACHING && (
            <div className={`${classes.toast} ${classes.toastDanger}`}>
              Mic not reaching listeners — toggle it off and on, or refresh if it persists.
            </div>
          )}
          {error && <div className={`${classes.toast} ${classes.toastDanger}`}>{error}</div>}
          {adQueue.notice && <div className={classes.toast}>{adQueue.notice}</div>}
        </div>

        <StudioRail items={railItems} />

        {/* Controls BELOW the video — never over it. Labels above the Net/Level indicators;
            no dividers; mic state via the coloured ring + word. */}
        {isConnected && (
          <div className={classes.controlBar}>
            {canTrackFromStudio && (
              <div className={classes.barSection}>
                <div className={classes.sectionHeader}>
                  <span className={classes.sectionLabel}>Scoreboard</span>
                  <ScoreboardSourcePicker
                    source={source}
                    options={sourceOptions}
                    onChange={chooseSource}
                    disabled={sourceMutation.isLoading}
                  />
                </div>
                <div className={classes.sectionBody}>
                  <ScoreboardControls
                    board={scoreboard.board}
                    connected={scoreboard.connected}
                    error={scoreboard.error}
                    locked={!manualScoreboard}
                    addPoints={scoreboard.addPoints}
                    setScore={scoreboard.setScore}
                    stepPeriod={scoreboard.stepPeriod}
                    setClockMs={scoreboard.setClockMs}
                    toggleClock={scoreboard.toggleClock}
                    resetClock={scoreboard.resetClock}
                  />
                </div>
              </div>
            )}

            {canTrackFromStudio && <div className={classes.barDivider} aria-hidden='true' />}

            <div className={classes.barSection}>
              <div className={classes.sectionHeader}>
                <span className={classes.sectionLabel}>Commentary</span>
              </div>
              <div className={classes.sectionBody}>
                <div className={classes.ctrlRow}>
                  <Tooltip title={micTooltip} arrow>
                    {/* span wrapper so the tooltip still shows while the Fab is disabled */}
                    <span className={classes.micButtonWrap}>
                      {isOnAir && (
                        <span
                          className={`${classes.micRing} ${ringStateClass}`}
                          aria-hidden='true'
                        />
                      )}
                      <Fab
                        className={`${classes.micFab} ${isOnAir ? classes.micFabOn : ''}`}
                        disabled={micDisabled}
                        onClick={onMicClick}
                        aria-label={isOnAir ? 'Mute microphone' : 'Go on air'}
                      >
                        {micBusy ? <CircularProgress size={24} color='inherit' /> : micIcon}
                      </Fab>
                      <span className={classes.micModeLabel}>{commentaryModeLabel}</span>
                    </span>
                  </Tooltip>
                  {isOnAir && (
                    <span
                      className={classes.ctrlStatus}
                      style={{ color: deliveryColor }}
                      role='status'
                      aria-live='polite'
                    >
                      {deliveryLabel}
                    </span>
                  )}
                </div>

                <div className={classes.labeledCtrl}>
                  <span className={classes.ctrlLabel}>Network</span>
                  <NetworkBars level={networkLevel} dark />
                </div>

                <div className={classes.labeledCtrl}>
                  <span className={classes.ctrlLabel}>Level</span>
                  <div className={classes.ctrlRow}>
                    <AudioLevelMeter track={isOnAir ? micTrack : null} width='110px' dark />
                    {gainSupported && (
                      <>
                        <Tooltip title='Mic volume' arrow>
                          <Slider
                            className={classes.gainSliderDark}
                            value={Math.round(micGain * 100)}
                            min={0}
                            max={MIC_GAIN_MAX * 100}
                            step={5}
                            marks={[{ value: 100 }]}
                            disabled={!isConnected}
                            onChange={(_e, v) => {
                              setMicGain((Array.isArray(v) ? v[0] : v) / 100)
                              setAdjusting(true)
                            }}
                            onChangeCommitted={() => setAdjusting(false)}
                            aria-label='Microphone level'
                          />
                        </Tooltip>
                        <span
                          className={`${classes.gainReadout} ${
                            micGain > 1 ? classes.gainReadoutBoost : ''
                          }`}
                          aria-live='polite'
                        >
                          {adjusting ? Math.round(micGain * 100) : micGain > 1 ? 'Boost' : ''}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <Tooltip
                  title={
                    previewMuted
                      ? 'Unmute stream audio'
                      : 'Mute stream audio (only affects what you hear)'
                  }
                  arrow
                >
                  <IconButton className={classes.muteBtnBar} onClick={togglePreviewMute}>
                    {previewMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Go on air?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your microphone will be mixed live into the stream — everyone watching will hear your
            commentary until you mute.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant='contained' color='primary' onClick={confirmGoOnAir}>
            Go on air
          </Button>
        </DialogActions>
      </Dialog>
    </ContainerPadding>
  )
}

StudioRoomView.propTypes = {
  joinInfo: PropTypes.shape({
    liveKitUrl: PropTypes.string,
    room: PropTypes.string,
    token: PropTypes.string,
    identity: PropTypes.string,
    commentaryMode: PropTypes.oneOf(['OffSite', 'OnSite']),
  }).isRequired,
  game: PropTypes.shape({
    broadcast: PropTypes.shape({
      scoreboardSource: PropTypes.string,
      supportedScoreboardSources: PropTypes.arrayOf(PropTypes.string),
      type: PropTypes.string,
    }),
    eventTeams: PropTypes.arrayOf(PropTypes.object),
    teams: PropTypes.arrayOf(PropTypes.object),
    media: PropTypes.arrayOf(PropTypes.object),
  }),
  orgId: PropTypes.string,
  gameId: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  onBack: PropTypes.func.isRequired,
  onTokenExpiring: PropTypes.func,
}

StudioRoomView.defaultProps = {
  game: null,
  orgId: null,
  gameId: null,
  title: '',
  subtitle: '',
  onTokenExpiring: null,
}

const StudioGamePage = () => {
  const classes = useStyles()
  const { gameId } = useParams()
  const location = useLocation()
  const { redirect } = useHistoryPush()
  const localStorageValue = useGetLocalStorage()
  const { [localStorageIds.SELECTED_ORG]: selectedOrg } = localStorageValue || {}
  const orgId = selectedOrg?.id
  const goBack = () => redirect('/studio')

  const {
    data: joinInfo,
    error,
    isFetching,
    refetch,
  } = useQuery(['getStudioJoinInfo', orgId, gameId], () => getStudioJoinInfo(orgId, gameId), {
    enabled: Boolean(orgId && gameId),
    retry: false,
    refetchOnWindowFocus: false,
    // Poll only while the stream isn't live yet (409); stop once we have join info.
    refetchInterval: (data, query) => {
      if (data) return false
      return query?.state?.error?.response?.status === 409 ? 8000 : false
    },
  })

  // Game title/subtitle for the header — reuse the operator's games list (cached by /studio).
  const { data: gamesData } = useQuery(['getStudioGames', orgId], () => getStudioGames(orgId), {
    enabled: Boolean(orgId),
    staleTime: 60000,
  })
  const game = useMemo(() => {
    // Prefer the game handed over via navigation state (instant + reliable when the
    // operator clicked through from /studio); fall back to the cached games list for
    // deep links / refreshes.
    const fromState = location.state?.studioGame
    if (fromState?.id === gameId) return fromState
    const all = [...(gamesData?.upcoming || []), ...(gamesData?.past || [])]
    return all.find((g) => g.id === gameId)
  }, [location.state, gamesData, gameId])
  const title = gameTitle(game)
  const subtitle = gameSubtitle(game)
  // Latch for the "waiting for the stream" state — see the conditional cascade below.
  const waitingForLiveRef = useRef(false)

  const status = error?.response?.status
  // react-query v3 clears `error` and flips `status` to 'loading' on every background
  // refetch of a query that has never succeeded (no dataUpdatedAt). The join-info poll
  // never succeeds until the stream goes live, so each 8s poll would otherwise tear the
  // page from the "waiting" stage down to the full-screen loader and back — a visible
  // blink. Latch the waiting state so background polls keep the stage mounted; clear it
  // only once we actually connect (joinInfo) or hit a settled error.
  if (status === 409) waitingForLiveRef.current = true
  else if (joinInfo || (error && !isFetching)) waitingForLiveRef.current = false

  // No permission or add-on to commentate (403) or not an assigned operator on this game (404).
  // 401 stays for backends that still answer a refused permission with it.
  if (status === 401 || status === 403 || status === 404) return <UnauthorizedPage />

  if (joinInfo) {
    return (
      <StudioRoomView
        joinInfo={joinInfo}
        game={game}
        orgId={orgId}
        gameId={gameId}
        title={title}
        subtitle={subtitle}
        onBack={goBack}
        onTokenExpiring={refetch}
      />
    )
  }

  // Stream not live yet: keep polling (above) and show the waiting state, held sticky
  // across the background polls (waitingForLiveRef) so the stage doesn't blink.
  if (status === 409 || waitingForLiveRef.current) {
    return (
      <ContainerPadding paddingValue='1.5%' vertical>
        <StudioTopBar title={title} subtitle={subtitle} onBack={goBack} />
        <div className={classes.stage}>
          <div className={classes.overlay}>
            <Typography variant='h6' className={classes.overlayText}>
              Waiting for the stream to start…
            </Typography>
            <Typography variant='body2' className={classes.overlayText}>
              This page connects automatically once the stream goes live.
            </Typography>
          </div>
        </div>
      </ContainerPadding>
    )
  }

  if (error) {
    return (
      <ContainerPadding paddingValue='1.5%' vertical>
        <StudioTopBar title={title} subtitle={subtitle} onBack={goBack} />
        <Typography variant='h6' className={classes.errorText}>
          Could not load the studio for this game.
        </Typography>
      </ContainerPadding>
    )
  }

  return <Loader />
}

export default StudioGamePage
